import React, { useEffect, useState } from 'react';
import { SearchIcon, MapPinIcon, UserIcon, ShoppingCartIcon, MenuIcon, TruckIcon } from 'lucide-react';
interface HeaderProps {
  onNavigate: (page: 'home' | 'shop' | 'product') => void;
  onOpenCart: () => void;
  onOpenMobileMenu: () => void;
}
export function Header({
  onNavigate,
  onOpenCart,
  onOpenMobileMenu
}: HeaderProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeCategory, setActiveCategory] = useState('Dog');
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const mainNavLinks = ['Dog', 'Cat', 'Other Animals', 'Garden', 'Pool', 'Services', 'Care Club'];
  const subcategories: Record<string, string[]> = {
    Dog: ['Dog Specials', 'Dog Food', 'Dog Treats', 'Dog Toys', 'Dog Health', 'Dog Accessories', 'Puppy', 'Dog Wash', 'New Dog', 'Advice'],
    Cat: ['Cat Specials', 'Cat Food', 'Cat Treats', 'Cat Toys', 'Cat Health', 'Cat Accessories', 'Kitten', 'Cat Litter', 'New Cat', 'Advice'],
    'Other Animals': ['Bird', 'Fish', 'Reptile', 'Small Animal', 'Large Animal', 'Poultry', 'Aquarium', 'Advice'],
    Garden: ['Garden Specials', 'Plants', 'Soil & Mulch', 'Fertiliser', 'Tools', 'Pots', 'Pest Control', 'Advice'],
    Pool: ['Pool Specials', 'Pool Chemicals', 'Pool Equipment', 'Testing Kits', 'Accessories', 'Advice'],
    Services: ['Dog Wash', 'Carry to Car', 'Pool Testing', 'Pond Testing', 'Advice'],
    'Care Club': ['Membership', 'Benefits', 'Join Now', 'Member Specials', 'Advice']
  };
  const activeSubs = subcategories[activeCategory] || [];
  return <header className="sticky top-0 z-40 w-full flex flex-col" data-id="element-448">
      <div className="bg-[#002faa] text-white flex flex-col" data-id="element-449">
        {/* ROW 1: Utility Bar */}
        <div className="border-b border-white/10" data-id="element-450">
          <div className="container-wide flex items-center justify-between py-1.5 md:py-2" data-id="element-451">
            {/* Mobile: simplified */}
            <div className="flex md:hidden w-full justify-center items-center gap-2 text-[11px]" data-id="element-452">
              <TruckIcon className="w-3 h-3 text-brand-green" data-id="element-453" />
              <span className="font-bold text-white" data-id="element-454">
                FREE Local Delivery Within 5km*
              </span>
            </div>

            {/* Desktop Left */}
            <div className="hidden md:flex items-center gap-2 text-sm" data-id="element-455">
              <img src="https://cdn.magicpatterns.com/uploads/cHcSSRvAwPTr86uE9Kk5yi/WA_OWned.webp" alt="WA Owned" className="w-5 h-5 rounded-full object-cover" data-id="element-456" />
              <span className="font-semibold text-white" data-id="element-457">
                Proudly WA Owned & Open 7 Days
              </span>
            </div>

            {/* Desktop Right */}
            <div className="hidden md:flex items-center gap-5" data-id="element-458">
              <div className="flex items-center gap-2 bg-brand-green rounded-full px-4 py-1.5 text-sm font-bold text-[#002faa]" data-id="element-459">
                <TruckIcon className="w-4 h-4" data-id="element-460" />
                <span data-id="element-461">FREE Local Delivery Within 5km*</span>
              </div>
              <button className="flex items-center gap-2 hover:text-brand-green-300 transition-colors active:scale-95" data-id="element-462">
                <MapPinIcon className="w-5 h-5 text-white/80" data-id="element-463" />
                <div className="flex flex-col items-start text-left" data-id="element-464">
                  <span className="text-[11px] text-white/60 leading-tight" data-id="element-465">
                    Your Store
                  </span>
                  <span className="text-sm font-bold text-white leading-tight" data-id="element-466">
                    Select Store
                  </span>
                </div>
              </button>
            </div>
          </div>
        </div>

        {/* ROW 2: Main Header — Logo | Nav (incl Care Club) | Search | Account + Cart */}
        <div className="py-2 md:py-3" data-id="element-467">
          <div className="container-wide flex items-center gap-4 lg:gap-6" data-id="element-468">
            {/* Mobile Layout */}
            <div className="flex md:hidden items-center justify-between w-full" data-id="element-469">
              <button className="p-2 -ml-2 text-white/80 hover:text-white active:scale-95 min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={onOpenMobileMenu} aria-label="Open menu" data-id="element-470">
                <MenuIcon className="w-6 h-6" data-id="element-471" />
              </button>
              <div className="cursor-pointer flex-shrink-0 active:scale-95 transition-transform" onClick={() => onNavigate('home')} data-id="element-472">
                <img src="https://cdn.magicpatterns.com/uploads/dmbfFx8UTLKDZ7YQXuKt3w/BPAG_website_logo.svg" alt="Better Pets and Gardens" className="h-7 object-contain drop-shadow-md" data-id="element-473" />
              </div>
              <button className="p-2 -mr-2 text-white/80 hover:text-white active:scale-95 relative min-w-[44px] min-h-[44px] flex items-center justify-center" onClick={onOpenCart} aria-label="Open cart" data-id="element-474">
                <ShoppingCartIcon className="w-6 h-6" data-id="element-475" />
                <span className="absolute top-1 right-1 w-4 h-4 bg-brand-green text-[#002faa] text-[10px] font-bold rounded-full flex items-center justify-center border border-[#002faa]" data-id="element-476">
                  2
                </span>
              </button>
            </div>

            {/* Desktop: Logo */}
            <div className="hidden md:block cursor-pointer flex-shrink-0 active:scale-95 transition-transform" onClick={() => onNavigate('home')} data-id="element-477">
              <img src="https://cdn.magicpatterns.com/uploads/dmbfFx8UTLKDZ7YQXuKt3w/BPAG_website_logo.svg" alt="Better Pets and Gardens" className="h-10 lg:h-12 object-contain drop-shadow-md" data-id="element-478" />
            </div>

            {/* Desktop: Nav Links (Care Club in green, after Services) */}
            <nav className="hidden md:flex items-center gap-1 lg:gap-2 flex-shrink-0" data-id="element-479">
              {mainNavLinks.map(link => <button key={link} onClick={() => {
              setActiveCategory(link);
              onNavigate('shop');
            }} className={`whitespace-nowrap px-3 py-2 text-base font-heading font-semibold transition-colors active:scale-95 ${link === 'Care Club' ? 'text-brand-green hover:text-brand-green-300' : activeCategory === link ? 'text-white' : 'text-white/80 hover:text-white'}`} data-id="element-480">
                  {link}
                </button>)}
            </nav>

            {/* Desktop: Search Bar */}
            <div className="hidden md:block flex-1 max-w-sm relative" data-id="element-481">
              <input type="text" placeholder="Search products, brands, advice..." className="w-full bg-white rounded-lg py-2 pl-9 pr-20 text-gray-900 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green min-h-[40px]" data-id="element-482" />
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-483" />
              <button className="absolute right-1 top-1/2 -translate-y-1/2 bg-brand-green text-[#002faa] px-4 py-1.5 rounded-md text-sm font-heading font-bold hover:bg-brand-green-600 transition-colors active:scale-95" data-id="element-484">
                Search
              </button>
            </div>

            {/* Desktop: Account + Cart (pushed to far right) */}
            <div className="hidden md:flex items-center gap-5 flex-shrink-0 ml-auto" data-id="element-485">
              <button className="flex items-center gap-2 group active:scale-95 min-h-[44px]" data-id="element-486">
                <UserIcon className="w-6 h-6 text-white/80 group-hover:text-white transition-colors" data-id="element-487" />
                <div className="flex flex-col items-start text-left" data-id="element-488">
                  <span className="text-[11px] text-white/60 leading-tight" data-id="element-489">
                    Account
                  </span>
                  <span className="text-sm font-heading font-bold text-white leading-tight group-hover:text-brand-green-300 transition-colors" data-id="element-490">
                    Sign In
                  </span>
                </div>
              </button>
              <button className="flex items-center gap-2 group active:scale-95 min-h-[44px]" onClick={onOpenCart} data-id="element-491">
                <div className="relative" data-id="element-492">
                  <ShoppingCartIcon className="w-6 h-6 text-white/80 group-hover:text-white transition-colors" data-id="element-493" />
                  <span className="absolute -top-2 -right-2 w-5 h-5 bg-brand-green text-[#002faa] text-xs font-bold rounded-full flex items-center justify-center border-2 border-[#002faa]" data-id="element-494">
                    2
                  </span>
                </div>
                <span className="text-sm font-heading font-bold text-white group-hover:text-brand-green-300 transition-colors" data-id="element-495">
                  $159.89
                </span>
              </button>
            </div>
          </div>

          {/* Mobile Search Bar */}
          <div className="md:hidden mt-2 px-4 pb-1" data-id="element-496">
            <div className="relative" data-id="element-497">
              <input type="text" placeholder="Search products, brands, advice..." className="w-full bg-white rounded-lg py-2 pl-10 pr-4 text-gray-900 text-sm focus:outline-none min-h-[40px]" data-id="element-498" />
              <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" data-id="element-499" />
            </div>
          </div>
        </div>
      </div>

      {/* Mobile: Category tabs */}
      <div className="md:hidden bg-[#002588] border-t border-white/10" data-id="element-500">
        <div className="flex overflow-x-auto scrollbar-hide px-2" data-id="element-501">
          {mainNavLinks.map(link => <button key={link} onClick={() => {
          setActiveCategory(link);
          onNavigate('shop');
        }} className={`whitespace-nowrap px-3.5 py-2 text-xs font-heading font-semibold transition-colors flex-shrink-0 active:scale-95 min-h-[40px] relative ${link === 'Care Club' ? 'text-brand-green' : activeCategory === link ? 'text-white' : 'text-white/60'}`} data-id="element-502">
              {link}
              {activeCategory === link && link !== 'Care Club' && <span className="absolute bottom-0 left-1 right-1 h-[2px] bg-brand-green rounded-full" data-id="element-503"></span>}
            </button>)}
        </div>
      </div>

      {/* ROW 3: Sub-category Bar — hides on scroll */}
      <div className={`bg-[#002faa] border-t-[3px] border-brand-green transition-all duration-300 overflow-hidden ${isScrolled ? 'max-h-0 opacity-0 border-t-0' : 'max-h-16 opacity-100'}`} data-id="element-504">
        <div className="container-wide" data-id="element-505">
          <div className="flex items-center justify-between" data-id="element-506">
            <ul className="flex items-center overflow-x-auto scrollbar-hide gap-0 py-2 md:py-2.5" data-id="element-507">
              {activeSubs.map(sub => <li key={sub} className="flex-shrink-0" data-id="element-508">
                  <button onClick={() => onNavigate('shop')} className="whitespace-nowrap px-2.5 md:px-3 lg:px-4 py-1 md:py-1.5 text-xs md:text-sm font-semibold text-white/90 hover:text-white transition-colors active:scale-95 min-h-[36px] flex items-center" data-id="element-509">
                    {sub}
                  </button>
                </li>)}
            </ul>
          </div>
        </div>
      </div>
    </header>;
}